'use client';
import React from 'react';
import styles from '../styles/AvailableSections.module.css';
import { formatTime, getActualStartDate, getActualEndDate } from '../SectionHelpers';
import { canManageSections } from '../../../utils/permissions';

export default function SectionCard({
  section,
  user,
  meetingsVisible,
  onToggleMeetings,
  onEditSection,
  onDeleteSection,
  onEnrollClick,
  seatsInputs,
  onSeatsInputChange,
  onUpdateSeats
}) {
  const sortedMeetings = [...(section.meetings || [])].sort(
    (a, b) => new Date(a.startTime) - new Date(b.startTime)
  );
  const actualStartDate = getActualStartDate(section);
  const actualEndDate = getActualEndDate(section);

  // Calculate section status based on dates
  const now = new Date();
  const startDate = section.startDate ? new Date(section.startDate) : actualStartDate;
  const endDate = section.endDate ? new Date(section.endDate) : actualEndDate;

  let sectionStatus = 'upcoming'; // default
  if (endDate && now >= endDate) {
    sectionStatus = 'completed';
  } else if (startDate && endDate && now >= startDate && now < endDate) {
    sectionStatus = 'in_session';
  }

  const headingDay = section.teachingDays
    ? section.teachingDays.split(',').join(', ')
    : sortedMeetings.length > 0
      ? new Date(sortedMeetings[0].startTime).toLocaleDateString('en-US', { weekday: 'long' })
      : 'Unknown';

  const headingTime = sortedMeetings.length > 0
    ? `${formatTime(sortedMeetings[0].startTime)} – ${formatTime(sortedMeetings[0].endTime)}`
    : 'No meetings scheduled';

  return (
    <div className={styles.timeSlot}>
      <div className={styles.timeSlotHeader}>
        <div>
          <h3>{headingDay}</h3>
          <span>{headingTime}</span>
        </div>
        <div className={styles.statusTag}>
          {sectionStatus === 'completed' && (
            <span className={styles.completedTag}>Completed</span>
          )}
          {sectionStatus === 'in_session' && (
            <span className={styles.inSessionTag}>In Session</span>
          )}
          {sectionStatus === 'upcoming' && (
            <span className={styles.upcomingTag}>Upcoming</span>
          )}
        </div>
      </div>

      <div className={styles.timeSlotDetails}>
        <p>
          <strong>Starts:</strong>{' '}
          {actualStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
        <p>
          <strong>Ends:</strong>{' '}
          {actualEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>

        {sortedMeetings.length >= 1 && (
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => onToggleMeetings(section.id)}
          >
            {meetingsVisible[section.id]
              ? `Hide ${sortedMeetings.length} Meetings`
              : `Show ${sortedMeetings.length} Meetings`}
          </button>
        )}

        {canManageSections(user, section.course) && (
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={() => onEditSection(section)}
          >
            Edit Section
          </button>
        )}

        {(meetingsVisible[section.id] || sortedMeetings.length === 0) && (
          <div className={styles.meetingsList}>
            {sortedMeetings.map((meeting, index) => (
              <p key={meeting.id || `meeting-${index}`}>
                {new Date(meeting.startTime).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}
                , {formatTime(meeting.startTime)} - {formatTime(meeting.endTime)}
              </p>
            ))}
          </div>
        )}
      </div>

      <div className={styles.timeSlotFooter}>
        {/* ONE clean line */}
        {canManageSections(user, section.course) && (
          <p className={styles.seatsFilledLine}>
            {section.enrolledLearnersCount} / {section.seatsTotal} seats filled
          </p>
        )}

        {canManageSections(user, section.course) && (
          <div className={styles.adminControls}>
            <label>
              Seats:
              <input
                type="number"
                className={styles.seatsInput}
                value={seatsInputs?.[section.id] ?? section.seatsTotal}
                min="1"
                max="8"
                onChange={(e) => onSeatsInputChange(section.id, e.target.value)}
              />
            </label>
            <button
              className={`${styles.btn} ${styles.updateSeatsBtn}`}
              onClick={() => onUpdateSeats(section.id)}
            >
              Update
            </button>
          </div>
        )}

        {canManageSections(user, section.course) && (
          <button
            className={`${styles.btn} ${styles.btnDanger}`}
            onClick={() => onDeleteSection(section.id)}
          >
            Delete Section
          </button>
        )}

        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={() => onEnrollClick(section)}
        >
          {/* Start Free class */}
          Enroll
        </button>
      </div>
    </div>
  );
}